import { useMemo, useState, type ReactNode } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select, { type MultiValue } from 'react-select';
import type { UserAccessRoleType } from '@econtractor/types';
import { useTheme } from 'context/ThemeContext';
import SolarIcon from 'components/icons/SolarIcon';
import EToolTip from 'components/others/EToolTip';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import TeamSelect from '../TeamSelect';
import {
    defaultUserListFilter,
    defaultUserRoles,
    isUserFilterActive,
    type UserListFilterState,
} from '../model/userListFilter';

type RoleOption = {
    value: UserAccessRoleType;
    label: string;
};

type StatusOption = {
    value: number;
    label: string;
};

const statusIcons: Record<string, ReactNode> = {
    '1': <SolarIcon name="check-circle" className="fs-14 text-success" />,
    '0': <SolarIcon name="x-circle" className="fs-14 text-danger" />,
};

interface UsersFilterProps {
    filter: UserListFilterState;
    onApply: (filter: UserListFilterState) => void;
}

const UsersFilter = ({ filter, onApply }: UsersFilterProps) => {
    const { t } = useTranslation();
    const { darkTheme } = useTheme();
    const [show, setShow] = useState(false);
    const [draft, setDraft] = useState<UserListFilterState>(filter);
    const selectTheme = darkTheme ? selectStylesDark : selectStylesLigth;
    const filterActive = isUserFilterActive(filter);

    const roleOptions = useMemo<RoleOption[]>(
        () =>
            defaultUserRoles.map((role) => ({
                value: role,
                label: t(`Users.roles.${role}`),
            })),
        [t],
    );

    const statusOptions = useMemo<StatusOption[]>(
        () => [
            { value: 1, label: t('Users.statActive') },
            { value: 0, label: t('Users.statBlock') },
        ],
        [t],
    );

    const selectedRoles = useMemo(
        () => roleOptions.filter((option) => draft.roles.includes(option.value)),
        [draft.roles, roleOptions],
    );

    const selectedStatuses = useMemo(
        () => statusOptions.filter((option) => draft.status.includes(option.value)),
        [draft.status, statusOptions],
    );

    const handleHide = () => setShow(false);

    const handleOpen = () => {
        setDraft({
            roles: [...filter.roles],
            teamId: filter.teamId,
            status: [...filter.status],
        });
        setShow(true);
    };

    const handleApply = () => {
        onApply({
            roles: [...draft.roles],
            teamId: draft.teamId,
            status: [...draft.status],
        });
        handleHide();
    };

    const handleReset = () => {
        onApply(defaultUserListFilter());
        handleHide();
    };

    const formatStatusOption = (option: StatusOption, { context }: { context: 'menu' | 'value' }) => {
        if (context === 'value') return option.label;
        return (
            <span className="d-inline-flex align-items-center gap-1">
                {statusIcons[String(option.value)]}
                <span>{option.label}</span>
            </span>
        );
    };

    return (
        <>
            <EToolTip title={t('Users.toolFilter')}>
                <span className="d-inline-block">
                    <Button
                        className="fs-14 px-2"
                        size="sm"
                        variant={filterActive ? 'danger' : 'primary'}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            handleOpen();
                        }}
                    >
                        <SolarIcon name="funnel" className="fs-14 p-0" />
                    </Button>
                </span>
            </EToolTip>

            <Offcanvas show={show} onHide={handleHide} placement="end">
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{t('Users.toolFilter')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-filter-form d-flex flex-column h-100">
                        <div className="off-filter-content px-3">
                            <div className="mb-3">
                                <label className="form-label text-muted fs-12 mb-1">{t('Users.filterRole')}</label>
                                <Select<RoleOption, true>
                                    isMulti
                                    isClearable
                                    isSearchable={false}
                                    options={roleOptions}
                                    value={selectedRoles}
                                    onChange={(options: MultiValue<RoleOption>) => {
                                        setDraft((prev) => ({
                                            ...prev,
                                            roles: (options || []).map((option) => option.value),
                                        }));
                                    }}
                                    placeholder={t('Users.filterRole')}
                                    className="w-100 object-filter-select"
                                    classNamePrefix="object-filter-select"
                                    styles={{
                                        ...selectTheme,
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 2100,
                                        }),
                                    }}
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                    menuShouldScrollIntoView={false}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label text-muted fs-12 mb-1">{t('Users.filterTeam')}</label>
                                <TeamSelect
                                    hideLabel
                                    isClearable
                                    showEmpty={false}
                                    placeholder={t('Users.filterTeam')}
                                    value={draft.teamId}
                                    onChange={(option) => {
                                        setDraft((prev) => ({
                                            ...prev,
                                            teamId: option?.value ? Number(option.value) : null,
                                        }));
                                    }}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label text-muted fs-12 mb-1">{t('Users.filterStatus')}</label>
                                <Select<StatusOption, true>
                                    isMulti
                                    isClearable
                                    isSearchable={false}
                                    options={statusOptions}
                                    value={selectedStatuses}
                                    onChange={(options: MultiValue<StatusOption>) => {
                                        setDraft((prev) => ({
                                            ...prev,
                                            status: (options || []).map((option) => option.value),
                                        }));
                                    }}
                                    placeholder={t('Users.filterStatus')}
                                    className="w-100 object-filter-select"
                                    classNamePrefix="object-filter-select"
                                    styles={{
                                        ...selectTheme,
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 2100,
                                        }),
                                    }}
                                    formatOptionLabel={formatStatusOption}
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                    menuShouldScrollIntoView={false}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                />
                            </div>
                        </div>
                        <div className="p-3 offcanvas-footer off-filter-footer">
                            <div className="d-flex justify-content-end gap-2">
                                <Button variant="secondary" onClick={handleReset}>
                                    {t('common:buttons.reset')}
                                </Button>
                                <Button variant="info" className="min100px" onClick={handleApply}>
                                    {t('common:buttons.apply')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default UsersFilter;
